import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Package,
  Users,
  Calendar,
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  ShoppingBag,
  LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { fetchKits, fetchStats, fetchAppointments, fetchOrders } from '../api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Admin = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [kits, setKits] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [statsData, setStatsData] = useState({ totalProducts: 0, totalOrders: 0, totalAppointments: 0, revenue: 0 });

  useEffect(() => {
    fetchKits().then(setKits).catch(() => setKits([]));
    fetchStats().then(setStatsData).catch(() => {});
    fetchAppointments().then(setAppointments).catch(() => setAppointments([]));
    fetchOrders().then(setOrders).catch(() => setOrders([]));
  }, []);

  const stats = [
    {
      title: 'Total Products',
      value: statsData.totalProducts,
      icon: Package,
      color: 'bg-blue-500'
    },
    {
      title: 'Total Orders',
      value: statsData.totalOrders,
      icon: ShoppingBag,
      color: 'bg-green-500'
    },
    {
      title: 'Appointments',
      value: statsData.totalAppointments,
      icon: Calendar,
      color: 'bg-purple-500'
    },
    {
      title: 'Revenue',
      value: `₹${statsData.revenue.toLocaleString('en-IN')}`,
      icon: TrendingUp,
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-center"
          >
            <div>
              <h1 className="text-5xl font-bold font-serif mb-2" data-testid="admin-dashboard-heading">Admin Dashboard</h1>
              <p className="text-muted-foreground">
                Manage your Ayurvedic business
                {admin?.email && (
                  <span className="ml-2 text-xs">• Signed in as <b>{admin.email}</b></span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button className="bg-primary hover:bg-primary/90" data-testid="admin-add-product-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add Product
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  logout();
                  navigate('/admin-login', { replace: true });
                }}
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="products">Products</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="appointments">Appointments</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-card rounded-2xl border border-border p-6 shadow-lg"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}
                    >
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold font-serif mb-1">{stat.value}</h3>
                  <p className="text-muted-foreground text-sm">{stat.title}</p>
                </motion.div>
              ))}
            </div>

            {/* Recent Activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card rounded-2xl border border-border p-6 shadow-lg"
              >
                <h2 className="text-2xl font-serif font-bold mb-4">Recent Orders</h2>
                <div className="space-y-3">
                  {orders.length === 0 && (
                    <p className="text-sm text-muted-foreground">No orders yet</p>
                  )}
                  {orders.slice(0, 4).map((order, i) => (
                    <div
                      key={order.id || i}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <span className="font-medium">{order.customerName || 'Order'}</span>
                        <span className="text-sm text-muted-foreground ml-2">₹{order.total}</span>
                      </div>
                      <Badge variant="secondary">{order.status || 'processing'}</Badge>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-card rounded-2xl border border-border p-6 shadow-lg"
              >
                <h2 className="text-2xl font-serif font-bold mb-4">Upcoming Appointments</h2>
                <div className="space-y-3">
                  {appointments.length === 0 && (
                    <p className="text-sm text-muted-foreground">No appointments yet</p>
                  )}
                  {appointments.slice(0, 4).map((appt, i) => (
                    <div
                      key={appt.id || i}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <span className="font-medium text-sm">{appt.name} - {appt.timeSlot}</span>
                      <Badge>{appt.date}</Badge>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6">
            <div className="flex justify-between items-center">
              <Input placeholder="Search products..." className="max-w-md" />
              <Button className="bg-primary hover:bg-primary/90">
                <Plus className="w-5 h-5 mr-2" />
                Add Product
              </Button>
            </div>

            <div className="bg-card rounded-2xl border border-border shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-semibold">Product</th>
                      <th className="text-left p-4 font-semibold">Category</th>
                      <th className="text-left p-4 font-semibold">Price</th>
                      <th className="text-left p-4 font-semibold">Stock</th>
                      <th className="text-left p-4 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kits.slice(0, 5).map((medicine) => (
                      <tr key={medicine.id} className="border-t border-border">
                        <td className="p-4">
                          <div className="flex items-center space-x-3">
                            <img
                              src={medicine.image}
                              alt={medicine.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <span className="font-medium">{medicine.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-muted-foreground">{medicine.category}</td>
                        <td className="p-4 font-semibold">₹{medicine.price}</td>
                        <td className="p-4">
                          <Badge variant={medicine.inStock ? 'secondary' : 'destructive'}>
                            {medicine.inStock ? 'In Stock' : 'Out of Stock'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-2">
                            <Button size="sm" variant="ghost">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            <div className="bg-card rounded-2xl border border-border shadow-lg p-6">
              <h2 className="text-2xl font-serif font-bold mb-6">All Orders</h2>
              <div className="space-y-4">
                {orders.length === 0 && (
                  <p className="text-sm text-muted-foreground">No orders yet</p>
                )}
                {orders.map((order, i) => (
                  <div key={order.id || i} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-semibold">{order.customerName}</p>
                      <p className="text-sm text-muted-foreground">
                        {order.items?.length || 0} item(s) • {order.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">₹{order.total}</p>
                      <Badge variant="secondary">{order.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments" className="space-y-6">
            <div className="bg-card rounded-2xl border border-border shadow-lg p-6">
              <h2 className="text-2xl font-serif font-bold mb-6">All Appointments</h2>
              <div className="space-y-4">
                {appointments.length === 0 && (
                  <p className="text-sm text-muted-foreground">No appointments yet</p>
                )}
                {appointments.map((appt, i) => (
                  <div key={appt.id || i} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-semibold">{appt.name}</p>
                      <p className="text-sm text-muted-foreground">{appt.practitionerName || 'Doctor'} • {appt.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{appt.date}</p>
                      <p className="text-sm text-muted-foreground">{appt.timeSlot}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;